/**
 * Plain-English explanations of every audit log `action`, grouped for the "Learn more"
 * popup on Settings → Audit log. There's no server-side registry of action types — each repo
 * module just writes its own `action: '...'` string at the point it logs — so this list is
 * the one place they're all named and explained together. Keep it in sync by hand when a new
 * `logEvent(...)` call is added elsewhere in server/src.
 */
export const EVENT_TYPE_GROUPS = [
  {
    label: 'Modes',
    items: [
      { action: 'mode.switch', description: 'The company was switched from one operating mode to another.' },
      { action: 'mode.switch.superseded', description: 'A previously scheduled mode switch was replaced by a new one.' },
      { action: 'mode.switch.cancelled', description: 'A scheduled mode switch was cancelled before it took effect.' },
      { action: 'tenant.mode.configure', description: "A mode's name, description, or enabled status was changed." },
    ],
  },
  {
    label: 'Schedule',
    items: [
      { action: 'slots.set', description: 'The number of open slots shown for a day was changed.' },
      { action: 'assignment.create', description: 'Someone was assigned to a shift.' },
      { action: 'assignment.update', description: "An assignment's job, label, team, or slot was edited." },
      { action: 'assignment.update.override', description: "An assignment's job, label, team, or slot was edited by someone other than its owning PM." },
      { action: 'assignment.delete', description: 'An assignment was removed.' },
      { action: 'assignment.delete.override', description: 'An assignment was removed by someone other than its owning PM.' },
      { action: 'assignment.move', description: 'An assignment was dragged to a different day, slot, or person.' },
      { action: 'assignment.cleared', description: 'An assignment was removed as part of a mode switch that cleared the outgoing schedule.' },
      { action: 'conflict.resolve', description: 'Somebody booked on a day they are away was marked as deliberate, so the board stops counting it as a conflict.' },
      { action: 'conflict.unresolve', description: 'A conflict that had been marked deliberate was put back on the list.' },
    ],
  },
  {
    label: 'Teams',
    items: [
      { action: 'cat.team.create', description: "A new team was created — this is the team structure that belongs to Group mode." },
      { action: 'cat.team.update', description: "A team's name, color, or coordinator was changed." },
      { action: 'cat.team.reorder', description: 'Teams were reordered.' },
      { action: 'cat.team.members', description: "A team's standing member roster was changed." },
      { action: 'cat.team.move_member', description: 'A person was moved to a different team for a single day, leaving the standing roster untouched.' },
    ],
  },
  {
    label: 'Notifications',
    items: [
      { action: 'notify.senders', description: 'The list of people allowed to send the schedule email was changed.' },
      { action: 'notify.sent', description: 'A schedule notification email was sent.' },
    ],
  },
  {
    label: 'Job types',
    items: [
      { action: 'jobtype.create', description: 'A new job type was added.' },
      { action: 'jobtype.update', description: 'A job type was renamed or changed.' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { action: 'mcp.connect', description: 'Somebody connected an AI assistant to their own account.' },
      { action: 'mcp.disconnect', description: 'A connection was ended, by the person who made it or by an owner.' },
      { action: 'mcp.refresh_replay', description: 'A connection presented a credential it should no longer hold, and was revoked. Worth asking the person about.' },
    ],
  },
  {
    label: 'Displays',
    items: [
      { action: 'display.create', description: 'A wall display was added.' },
      { action: 'display.update', description: "A wall display's settings were changed." },
      { action: 'display.pairing.create', description: 'A pairing code was generated to connect a new wall display.' },
      { action: 'display.paired', description: 'A wall display was successfully connected using a pairing code.' },
      { action: 'display.delete', description: 'A wall display was deleted. It goes dark within a minute and has to be paired again from scratch.' },
      // Kept for history: revoking was replaced by deleting, so nothing writes this any
      // more, but events from before that still have to read as something.
      { action: 'display.revoke', description: "A wall display's access was revoked. (Superseded by deleting.)" },
    ],
  },
  {
    label: 'Time off',
    items: [
      { action: 'timeoff.create', description: 'Time off was recorded for someone.' },
      { action: 'timeoff.update', description: 'A time off entry was changed.' },
      { action: 'timeoff.delete', description: 'A time off entry was removed.' },
    ],
  },
  {
    label: 'Holidays',
    items: [
      { action: 'holiday.unmark', description: 'A statutory holiday was marked as not observed by this company, so it no longer shows on the board.' },
      { action: 'holiday.restore', description: 'A statutory holiday that had been dismissed was put back on the board.' },
    ],
  },
  {
    label: 'Assets',
    items: [
      { action: 'asset.create', description: 'An asset (a vehicle, a tool, a laptop) was added to the register.' },
      { action: 'asset.update', description: "An asset's name, description, groups, service dates or retired status were changed." },
      { action: 'asset.reorder', description: "The order of a group's assets was changed." },
      { action: 'asset.delete', description: 'An asset that had never been used was deleted from the register. The entry records what it was.' },
      { action: 'assetgroup.create', description: 'An asset group was created.' },
      { action: 'assetgroup.update', description: "An asset group's name or icon was changed." },
      { action: 'assetgroup.reorder', description: 'The order of the asset groups was changed.' },
      { action: 'assetgroup.delete', description: 'An asset group was deleted. Its assets were kept.' },
      { action: 'asset.custody', description: "An asset's standing operator was changed from a given date." },
      { action: 'asset.assign', description: 'Who has an asset on a day was set — a person, an outside party, or nobody.' },
      { action: 'asset.clear', description: "A day's exception was removed, handing the asset back to its standing operator." },
      { action: 'asset.downtime.create', description: 'An asset was taken out of service for a range of days.' },
      { action: 'asset.downtime.update', description: "An out-of-service period's dates, reason or repair notes were changed." },
      { action: 'asset.downtime.delete', description: 'An out-of-service period was removed.' },
    ],
  },
  {
    label: 'Lists',
    items: [
      { action: 'list.create', description: 'A list was set up.' },
      { action: 'list.update', description: "A list's name, description, colour, icon, what it shows beside each name, or retired status was changed." },
      { action: 'list.reorder', description: 'The lists were put in a different order.' },
      { action: 'list.entry.add', description: 'Somebody was put on a list for a day.' },
      { action: 'list.entry.update', description: "The note beside somebody's name on a list was changed." },
      { action: 'list.entry.move', description: 'Somebody on a list was moved to another day.' },
      { action: 'list.entry.remove', description: 'Somebody was taken off a list for a day.' },
    ],
  },
  {
    label: 'People',
    items: [
      { action: 'user.claim', description: 'Someone claimed an invited account and signed in for the first time.' },
      { action: 'user.create', description: 'A new user was added.' },
      { action: 'user.update', description: "A user's details were changed." },
      { action: 'user.roles', description: "A user's permission roles were changed." },
      { action: 'user.job_types', description: 'The job types a user can be scheduled for were changed.' },
      { action: 'pm.assistant', description: "A project manager's backup assistant was set or cleared." },
      { action: 'pm.team', description: "A project manager's standing team roster was changed." },
      { action: 'user.identity.unlink', description: 'A Microsoft sign-in was disconnected from a user.' },
      { action: 'user.merge', description: 'Two duplicate user accounts were combined into one.' },
    ],
  },
  {
    label: 'Data',
    items: [
      { action: 'backup.create', description: 'The nightly backup of everything this company keeps was taken.' },
      { action: 'backup.export', description: 'An owner took a fresh export on demand.' },
      { action: 'backup.download', description: 'An owner downloaded a backup — the whole dataset, in one file.' },
      { action: 'backup.safety', description: 'A copy of the data as it stood was taken automatically, just before an import replaced it.' },
      { action: 'backup.import', description: "An owner imported an export, replacing the company's data with its contents — another company's, if the entry names one." },
    ],
  },
  {
    label: 'Company settings',
    items: [
      { action: 'tenant.create', description: 'The company account was created.' },
      { action: 'tenant.branding', description: "The company's branding (logo, colors) was changed." },
      { action: 'tenant.settings', description: "The company's general scheduling settings were changed." },
      { action: 'cat.settings', description: "Group mode's standing configuration was changed." },
    ],
  },
]
